import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Card,
  Alert,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import {
  generateBusinessRegistrationQR,
  downloadQRCode,
  copyQRUrlToClipboard,
} from "../../utils/qr-generator";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const QRCodeGenerator = () => {
  const businesprofile = useSelector((state) => state.businesprofile);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const qrImageRef = useRef(null);

  useEffect(() => {
    console.log("Business profile in QRCodeGenerator:", businesprofile);
    // Auto-generate QR code when component loads and business profile is available
    if (businesprofile && businesprofile._id) {
      handleGenerateQR();
    }
  }, [businesprofile]);

  const handleGenerateQR = async () => {
    if (!businesprofile || !businesprofile._id) {
      setError("Business profile not found. Please ensure you are logged in.");
      toast.error("Business profile not found");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use business profile ID as the token for the URL
      const result = await generateBusinessRegistrationQR(businesprofile._id);
      setQrData(result);
      toast.success("QR Code generated successfully!");
    } catch (err) {
      setError("Failed to generate QR code. Please try again.");
      toast.error("Failed to generate QR code");
      console.error("QR generation error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    if (qrData && qrData.qrCodeDataURL) {
      const filename = `business-registration-qr-${qrData.token}.png`;
      downloadQRCode(qrData.qrCodeDataURL, filename);
      toast.success("QR code downloaded successfully!");
    }
  };

  const handleCopyURL = async () => {
    if (qrData && qrData.url) {
      const success = await copyQRUrlToClipboard(qrData.url);
      if (success) {
        toast.success("URL copied to clipboard!");
      } else {
        toast.error("Failed to copy URL to clipboard");
      }
    }
  };

  return (
    <div className="container-fluid mt-4">
      <Row>
        <Col lg={8} md={10} sm={12} className="mx-auto">
          <Card>
            <Card.Header>
              <h4 className="mb-0">
                Business Registration QR Code
              </h4>
              <small className="text-muted">
                Your unique QR code for external business registration access
              </small>
            </Card.Header>

            <Card.Body>
              {error && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}

              {/* Loading state */}
              {loading && (
                <div className="text-center py-4">
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  <span>Generating QR Code...</span>
                </div>
              )}

              {/* QR Code Display */}
              {qrData && !loading && (
                <div className="mt-4 text-center">
                  <h5>Your Business Registration QR Code</h5>

                  <div className="mb-3">
                    <img
                      ref={qrImageRef}
                      src={qrData.qrCodeDataURL}
                      alt="Business Registration QR Code"
                      style={{
                        maxWidth: "300px",
                        width: "100%",
                        border: "2px solid #dee2e6",
                        borderRadius: "12px",
                        padding: "15px",
                        backgroundColor: "white",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                  </div>

                  <div className="mb-4 p-3 bg-light rounded">
                    <h6 className="mb-2">QR Code Details</h6>
                    <small className="text-muted d-block mb-1">
                      <strong>Encoded Token:</strong> <code>{qrData.token}</code>
                    </small>
                    {qrData.originalBusinessId && (
                      <small className="text-muted d-block mb-1">
                        <strong>Original Business ID:</strong> <code>{qrData.originalBusinessId}</code>
                      </small>
                    )}
                    <small className="text-muted d-block mb-1">
                      <strong>Generated:</strong> {new Date(qrData.generatedAt).toLocaleString()}
                    </small>
                    <small className="text-muted d-block">
                      <strong>Registration URL:</strong>{" "}
                      <a
                        href={qrData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-break"
                      >
                        {qrData.url}
                      </a>
                    </small>
                  </div>

                  <div className="d-grid gap-2 d-md-flex justify-content-md-center">
                    <Button 
                      variant="success" 
                      onClick={handleDownloadQR}
                      size="lg"
                    >
                      📥 Download QR Code
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      onClick={handleCopyURL}
                      size="lg"
                    >
                      📋 Copy URL
                    </Button>
                  </div>

                </div>
              )}

              {/* No Business Profile Warning */}
              {!businesprofile && (
                <Alert variant="warning">
                  <Alert.Heading>Business Profile Required</Alert.Heading>
                  <p>
                    To generate a QR code, you need to have a business profile set up. 
                    Please complete your business information first.
                  </p>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default QRCodeGenerator;
